import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from '@angular/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding, withDisabledInitialNavigation, withEnabledBlockingInitialNavigation, withHashLocation, withRouterConfig } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserUtils, IPublicClientApplication, PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { MsalModule, MsalGuard, MsalService, MsalBroadcastService, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, MsalInterceptorConfiguration, MsalInterceptor } from '@azure/msal-angular';
import { NgxEchartsModule } from 'ngx-echarts';
import { Title } from '@angular/platform-browser';
import { provideServiceWorker } from '@angular/service-worker';
import { registerLocaleData } from '@angular/common';
import localeJa from '@angular/common/locales/ja';
import localeEn from '@angular/common/locales/en';
import localeZhHantTW from '@angular/common/locales/zh-Hant';

import { appRoutes } from './app.routes';
import { environment } from '../environments/environment';
import { b2cPolicies, apiConfig, b2cAuthorityMetadata } from '../environments/b2c-config';
import { getRedirectUri } from './utils';

import { RegisterInvitedUserService } from './data/util/register-invited-user.service';
import { InviteMembersService } from './data/member/invite-members.service';
import { ListUsersService } from './data/user/list-users.service';
import { ListMembersService } from './data/member/list-members.service';
import { StoreAuthService } from './core/auth/store-auth.service';
import { StoreUserService } from './core/state/store-user.service';
import { GetUseritselfService } from './data/user/get-useritself.service';
import { ListRecordsService } from './data/record/list-records.service';
import { ListRecordsDownloadRequestsService } from './data/record/list-records-download-requests.service';
import { GetRecordService } from './data/record/get-record.service';
import { GetUserSummaryService } from './data/user/get-user-summary.service';
import { GetSectionSummaryService } from './data/section/get-section-summary.service';
import { GetMemberService } from './data/member/get-member.service';
import { CreateMemberService } from './data/member/create-member.service';
import { GetUserService } from './data/user/get-user.service';
import { GetUserAndBelongsService } from './data/user/get-user-and-belongs.service';
import { UpdateMemberService } from './data/member/update-member.service';
import { UpdateUserService } from './data/user/update-user.service';
import { ContributorAccessService } from './data/belongs/contributor-access.service';
import { LinkDocumentService } from './data/util/link-document.service';
import { CreateUserService } from './data/user/create-user.service';
import { CreateRecordsDownloadRequestService } from './data/record/create-records-download-request.service';
import { GetRecordsWithFilesSizeService } from './data/record/get-records-with-files-size.service';
import { SidenavService } from './core/layout/sidenav.service';
import { ListFollowUpsService } from './data/dialog/list-follow-ups.service';
import { CreateFollowUpService } from './data/dialog/create-follow-up.service';
import { UpdateFollowUpService } from './data/dialog/update-follow-up.service';
import { DownloadFilesService } from './data/media/download-files.service';
import { DuplicateCheckService } from './data/util/duplicate-check.service';
import { DatabaseCacheService } from './data/cache/database-cache.service';
import { ListDialogsService } from './data/dialog/list-dialogs.service';
import { ListUserDataService } from './data/user/list-user-data.service';
import { ListMentionsService } from './data/mention/list-mentions.service';
import { CreateMentionService } from './data/mention/create-mention.service';
import { GeneratePdfDataService } from './data/util/generate-pdf-data.service';
import { GetSectionService } from './data/section/get-section.service';
import { ListSectionsService } from './data/section/list-sections.service';
import { UpdateSectionService } from './data/section/update-section.service';
import { CreateSectionService } from './data/section/create-section.service';
import { ListSectionGroupAdminsService } from './data/section-group/list-section-group-admins.service';
import { CreateSectionGroupAdminService } from './data/section-group/create-section-group-admin.service';
import { UpdateSectionGroupAdminService } from './data/section-group/update-section-group-admin.service';
import { CreateSectionGroupService } from './data/section-group/create-section-group.service';
import { UpdateSectionGroupService } from './data/section-group/update-section-group.service';
import { ListSectionGroupsService } from './data/section-group/list-section-groups.service';
import { CreateSystemAdminService } from './data/system-admin/create-system-admin.service';
import { UpdateSystemAdminService } from './data/system-admin/update-system-admin.service';
import { ListSystemAdminsService } from './data/system-admin/list-system-admins.service';
import { CreateDistributorService } from './data/distributor/create-distributor.service';
import { UpdateDistributorService } from './data/distributor/update-distributor.service';
import { GetDistributorService } from './data/distributor/get-distributor.service';
import { ListDistributorsService } from './data/distributor/list-distributors.service';
import { AssignDistributorAgentService } from './data/distributor/assign-distributor-agent.service';
import { RevokeDistributorAgentService } from './data/distributor/revoke-distributor-agent.service';
import { ListDistributorAgentsService } from './data/distributor/list-distributor-agents.service';
import { CreateBillToInfoService } from './data/bill-to-info/create-bill-to-info.service';
import { UpdateBillToInfoService } from './data/bill-to-info/update-bill-to-info.service';
import { GetBillToInfoService } from './data/bill-to-info/get-bill-to-info.service';
import { ListBillToInfoService } from './data/bill-to-info/list-bill-to-info.service';

import { UpdateParameterService } from './data/record/update-parameter.service';
import { GetParameterService } from './data/record/get-parameter.service';
import { ListSectionMonthlySummariesService } from './data/section/list-section-monthly-summaries.service';
import { ListSectionGroupMonthlySummariesService } from './data/section-group/list-section-group-monthly-summaries.service';
import { CreateBelongsService } from './data/belongs/create-belongs.service';
import { UserRolesGuard } from './core/guards/user-roles.guard';
import { SystemAdminGuard } from './core/guards/system-admin.guard';
import { SectionGroupAdminGuard } from './core/guards/section-group-admin.guard';
import { ContractStatusGuard } from './core/guards/contract-status.guard';
import { UserDataTemplateListComponent } from './features/admin-system/user-data-template-list/user-data-template-list.component';
import { firstValueFrom } from 'rxjs';
import { AuthFlowService } from './core/auth/auth-flow.service';
import { CustomRouteReuseStrategy } from './route-reuse.strategy';
import { ThemeService } from './core/layout/theme.service';
import { AppBadgeService } from './core/state/app-badge.service';
import { JapaneseDatepickerIntl } from './i18n';
import { ErrorHandlerInterceptor } from './error-handler.interceptor';

registerLocaleData(localeJa);
registerLocaleData(localeEn);
registerLocaleData(localeZhHantTW, 'zh-TW');

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
}

export function MSALInstanceFactory(): IPublicClientApplication {

  const currentHostname = window.location.hostname;

  const currentPort = window.location.port || '80';
  const currentPortStr = currentPort !== null ? ':' + currentPort : '';
  const currentProtocol = window.location.protocol;

  const redirectUri = getRedirectUri();

  return new PublicClientApplication({
    auth: {
      clientId: environment.clientID,
      authority: b2cPolicies.authorities.signUpSignIn.authority,
      authorityMetadata: b2cAuthorityMetadata,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      knownAuthorities: [b2cPolicies.authorityDomain],
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(apiConfig.uri, apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...apiConfig.scopes],
    },
  };
}

export function initializeMsal(msalService: MsalService): () => Promise<void> {
  return () => firstValueFrom(msalService.initialize());
}

export function initializeAuthFlow(authFlow: AuthFlowService): () => Promise<void> {
  return () => authFlow.init();
}

export function initializeTheme(themeService: ThemeService): () => void {
  return () => themeService.init();
}

const initialNavigationFeature = (BrowserUtils.isInIframe() || BrowserUtils.isInPopup())
  ? withDisabledInitialNavigation()
  : withEnabledBlockingInitialNavigation();

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(appRoutes, withHashLocation(), withComponentInputBinding(), initialNavigationFeature, withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    // Datepicker labels are locale-aware via MatDatepickerIntl
    { provide: MatDatepickerIntl, useClass: JapaneseDatepickerIntl },
    Title,
    RegisterInvitedUserService,
    InviteMembersService,
    ListUsersService,
    ListMembersService,
    StoreAuthService,
    StoreUserService,
    GetUseritselfService,
    ListRecordsService,
    ListRecordsDownloadRequestsService,
    GetRecordService,
    GetUserSummaryService,
    GetSectionSummaryService,
    GetMemberService,
    CreateMemberService,
    GetUserService,
    GetUserAndBelongsService,
    UpdateMemberService,
    UpdateUserService,
    ContributorAccessService,
    LinkDocumentService,
    CreateUserService,
    CreateRecordsDownloadRequestService,
    GetRecordsWithFilesSizeService,
    SidenavService,
    ListFollowUpsService,
    CreateFollowUpService,
    UpdateFollowUpService,
    DownloadFilesService,
    DuplicateCheckService,
    DatabaseCacheService,
    ListDialogsService,
    ListUserDataService,
    ListMentionsService,
    CreateMentionService,
    GeneratePdfDataService,
    GetSectionService,
    ListSectionsService,
    UpdateSectionService,
    CreateSectionService,
    ListSectionGroupAdminsService,
    CreateSectionGroupAdminService,
    UpdateSectionGroupAdminService,
    CreateSectionGroupService,
    UpdateSectionGroupService,
    ListSectionGroupsService,
    CreateSystemAdminService,
    UpdateSystemAdminService,
    ListSystemAdminsService,
    CreateDistributorService,
    UpdateDistributorService,
    GetDistributorService,
    ListDistributorsService,
    AssignDistributorAgentService,
    RevokeDistributorAgentService,
    ListDistributorAgentsService,
    CreateBillToInfoService ,
    UpdateBillToInfoService,
    GetBillToInfoService,
    ListBillToInfoService,
    UpdateParameterService,
    GetParameterService,
    UserDataTemplateListComponent,
    ListSectionMonthlySummariesService,
    ListSectionGroupMonthlySummariesService,
    CreateBelongsService,
    UserRolesGuard,
    SystemAdminGuard,
    SectionGroupAdminGuard,
    ContractStatusGuard,
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    MsalService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthFlow,
      deps: [AuthFlowService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeTheme,
      deps: [ThemeService],
      multi: true,
    },
    MsalGuard,
    MsalBroadcastService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    importProvidersFrom(
      MsalModule,
      NgxEchartsModule.forRoot({
        echarts: () => import('echarts'),
      })
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: 'serviceWorker' in navigator,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
